/**
 * Story 12.2 — derived-on-save listing fields.
 *
 * The create-listing wizard stops rendering form fields for three Console
 * listing fields and derives them from their source data on every save
 * instead:
 *
 *   - publicData.incentiveSearch  ← flatten of publicData.incentivePrograms
 *   - publicData.ageGroup         ← band of publicData.birthYear
 *   - publicData.locationState    ← match of publicData.location.address
 *
 * All functions are pure and side-effect free: no SDK, no config imports —
 * enum options are passed in by the caller (the EditListingPage duck thunks,
 * which have the merged config at call time).
 *
 * Shared contract: a derived key is touched only when its source key is
 * present in the payload, derivation failure leaves any existing value alone,
 * and nothing here ever throws on malformed input — a failed derivation must
 * never block a save.
 */

// Sane validity window for a horse's birth year. The floor is a validity
// bound (Rails' 2000 was only a query default); the ceiling matches Rails'
// default_birth_year_to = current year + 1 (a foal expected "next year" is
// still unborn — the foal_in_utero band).
const BIRTH_YEAR_FLOOR = 1980;

/**
 * Resolve a single incentive-program entry to a searchable name.
 * Mirrors the tph-api backfill's programToName: object entries fall back
 * through name → label → program; an opaque `id` alone contributes nothing.
 *
 * @param {*} program one entry of the incentivePrograms array
 * @returns {String} trimmed name, or '' when the entry yields none
 */
const programToName = program => {
  if (program === undefined || program === null) return '';
  if (typeof program === 'string') return program.trim();
  if (typeof program === 'object') {
    const value = program.name || program.label || program.program || '';
    return String(value).trim();
  }
  return String(program).trim();
};

/**
 * Flatten an incentivePrograms array into the plaintext keyword-search
 * mirror, re-implementing the tph-api backfill rules so the runtime writer
 * and the corpus writer converge on byte-identical strings: names trimmed,
 * empties dropped, sorted with the default code-unit sort
 * (locale-independent), joined with a single space.
 *
 * @param {*} incentivePrograms expected array; non-array input → ''
 * @returns {String} deterministic flattened string
 */
export const flattenIncentivePrograms = incentivePrograms => {
  if (!Array.isArray(incentivePrograms)) return '';
  return incentivePrograms
    .map(programToName)
    .filter(name => name !== '')
    .sort()
    .join(' ');
};

/**
 * Map a birth year to the Console ageGroup enum option. A future birth year
 * (strictly greater than currentYear, ceiling currentYear+1) means the foal
 * is still unborn → foal_in_utero (12-2 review FIX-1 product decision:
 * migrated in-utero listings recompute to the SAME value until the foal is
 * born, then naturally flip to zero_to_one). Born years use the Rails band
 * rules (listings_query.rb): birthYear ≥ currentYear−1 → zero_to_one,
 * −3..−2 → two_to_three, −5..−4 → four_to_five, −8..−6 → six_to_eight,
 * ≤ −9 → nine_plus.
 *
 * Only a number or a numeric string is accepted — arrays/objects/booleans
 * never coerce into a band (garbage in must not derive out).
 *
 * @param {*} birthYear number or numeric string
 * @param {Object} params
 * @param {Number} params.currentYear
 * @param {Array<Object>} params.enumOptions hosted [{ option, label }] for ageGroup
 * @returns {String|undefined} the option value, or undefined when the year is
 *   missing/unparseable/outside the sane window or the option is not in the schema
 */
export const deriveAgeGroup = (birthYear, { currentYear, enumOptions } = {}) => {
  const isYearCandidate =
    typeof birthYear === 'number' || (typeof birthYear === 'string' && birthYear.trim() !== '');
  const year = isYearCandidate ? Number(birthYear) : NaN;
  const now = Number(currentYear);
  if (!Number.isInteger(year) || !Number.isInteger(now)) return undefined;
  if (year < BIRTH_YEAR_FLOOR || year > now + 1) return undefined;

  const band =
    year > now
      ? 'foal_in_utero'
      : year >= now - 1
      ? 'zero_to_one'
      : year >= now - 3
      ? 'two_to_three'
      : year >= now - 5
      ? 'four_to_five'
      : year >= now - 8
      ? 'six_to_eight'
      : 'nine_plus';

  const options = Array.isArray(enumOptions) ? enumOptions : [];
  return options.some(o => o?.option === band) ? band : undefined;
};

// Strip a trailing US ZIP ("Austin, Texas 78701, United States") or Canadian
// postal code ("Toronto, Ontario M5V 2T6, Canada") so street-address picks
// still whole-segment match the state/province name.
const stripTrailingPostalCode = segment =>
  segment.replace(/\s+(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, '');

// 12-2 review NIT-1: D.C. is not a locationState enum option, and a
// "Washington, District of Columbia, …" address would otherwise single-match
// Washington state — the one US locale the ambiguity guard cannot catch
// (the true region is absent from the enum, so the city-name match wins alone).
const DC_SEGMENT = 'district of columbia';

// 🔴 Story 12.28 (cold review 2026-09-18, MEDIUM-5) — fold accents before
// matching. Google Places and Mapbox both return `Québec` for French locales,
// and Canada is a supported market, so a Québec listing matched no option at all
// and silently carried NO `locationState` — dropping out of state-filtered
// search. The sanitizer now resolves the region by an accent-folded catalogue
// match, so without this the raw and the sanitized address would derive
// DIFFERENT answers, which is exactly what AC7 forbids.
const foldAccents = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Derive the Console locationState enum option from a location pick's address
 * string (e.g. "Austin, Texas, United States"): split on commas, trim, and
 * match each whole segment case-insensitively against both the option labels
 * (full names — "Texas") and option values (two-letter codes — "tx").
 * Whole-segment comparison keeps "Kansas City" from matching Kansas.
 *
 * @param {*} address the address string from publicData.location.address
 * @param {Object} params
 * @param {Array<Object>} params.enumOptions hosted [{ option, label }] for locationState
 * @returns {String|undefined} the option value on an unambiguous single match,
 *   otherwise undefined
 */
export const deriveLocationState = (address, { enumOptions } = {}) => {
  if (typeof address !== 'string' || address.trim() === '') return undefined;
  const options = Array.isArray(enumOptions) ? enumOptions : [];
  if (options.length === 0) return undefined;

  const segments = address
    .split(',')
    .map(segment => segment.trim())
    .filter(segment => segment !== '');

  const isDC = segments.some(
    segment =>
      foldAccents(stripTrailingPostalCode(segment))
        .trim()
        .toLowerCase() === DC_SEGMENT
  );
  if (isDC) return undefined;

  const matches = new Set();
  segments.forEach(segment => {
    const candidates = [segment, stripTrailingPostalCode(segment)];
    candidates.forEach(candidate => {
      const normalized = foldAccents(candidate.trim().toLowerCase());
      if (normalized === '') return;
      options.forEach(o => {
        const optionValue =
          typeof o?.option === 'string' ? foldAccents(o.option.toLowerCase()) : null;
        const label = typeof o?.label === 'string' ? foldAccents(o.label.toLowerCase()) : null;
        if (normalized === optionValue || normalized === label) {
          matches.add(o.option);
        }
      });
    });
  });

  return matches.size === 1 ? matches.values().next().value : undefined;
};

const findEnumOptions = (config, key) => {
  const listingFields = config?.listing?.listingFields;
  const field = Array.isArray(listingFields) ? listingFields.find(f => f?.key === key) : null;
  return field?.enumOptions || [];
};

/**
 * Enrich an outgoing listing save payload with the derived fields. Runs in
 * both central save thunks (create draft + update) so every panel — current
 * and future wizard steps — gets derivation with no extra wiring.
 *
 * Rules, each idempotent and write-only-when-source-present:
 *   - incentiveSearch recomputed whenever publicData contains the
 *     incentivePrograms key (present-but-empty/null → '', matching the
 *     backfill's repair semantics)
 *   - ageGroup recomputed whenever publicData contains a birthYear key,
 *     written only when derivation yields a valid schema option
 *   - locationState recomputed whenever publicData contains location.address,
 *     written only on an unambiguous match
 *
 * A key present with the value `undefined` counts as ABSENT (12-2 review
 * FIX-2): the SDK's JSON serialization drops undefined keys, so the server's
 * source value survives such a save — the mirror must survive with it.
 *
 * Derived keys are set on top of the payload's publicData (overriding any
 * carried-forward stale mirror). Payloads with no publicData pass through
 * unchanged. The incoming payload is never mutated. Never throws.
 *
 * @param {Object} data outgoing save payload (may contain publicData)
 * @param {Object} config merged marketplace config (for hosted enumOptions)
 * @returns {Object} the enriched payload, or the original when nothing applies
 */
export const applyDerivedListingFields = (data, config) => {
  try {
    const publicData = data?.publicData;
    if (!publicData || typeof publicData !== 'object' || Array.isArray(publicData)) {
      return data;
    }

    const derived = {};

    if (publicData.incentivePrograms !== undefined) {
      derived.incentiveSearch = flattenIncentivePrograms(publicData.incentivePrograms);
    }

    if (publicData.birthYear !== undefined) {
      const ageGroup = deriveAgeGroup(publicData.birthYear, {
        currentYear: new Date().getFullYear(),
        enumOptions: findEnumOptions(config, 'ageGroup'),
      });
      if (ageGroup !== undefined) {
        derived.ageGroup = ageGroup;
      }
    }

    const address = publicData.location?.address;
    if (address !== undefined) {
      const locationState = deriveLocationState(address, {
        enumOptions: findEnumOptions(config, 'locationState'),
      });
      if (locationState !== undefined) {
        derived.locationState = locationState;
      }
    }

    return Object.keys(derived).length > 0
      ? { ...data, publicData: { ...publicData, ...derived } }
      : data;
  } catch (e) {
    // Derivation must never block a save — fall back to the untouched payload.
    // Dev-only signal (12-2 review NIT-2): a silent no-op here would disable
    // all three derivations with zero trace. No util/log import — the module
    // stays pure; production saves stay quiet.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('applyDerivedListingFields: derivation failed, saving payload underived', e);
    }
    return data;
  }
};
